/**
 * Pure helpers for the Image studio (PR 16, plan P9/P10): the product page's
 * Images tab and the Pending changes tray's handling of listing-image rows.
 * Tested in imageStudio.test.ts; no React, no fetch.
 *
 * The server stays the authority on everything here. `clientBlockers` mirrors
 * backend/media/compliance.approval_blockers so the Approve button can stay
 * disabled while something is missing, and `estimateCredits` mirrors
 * backend/media/pixii.estimate_credits so the cost the person confirms is the
 * one the server checks (`confirm_credits`; a mismatch is a 409).
 */
import type { ManualEdit } from './api'
import { fmtInt } from './format'
import { fmtClockTime, fmtMonthDayTime, parseServerTimestamp } from './format/date'
import type {
  JobKind, LiveSlot, MediaCandidate, MediaCheck, MediaJob, MediaStatus, RequiredTick, Slot,
} from './imageStudioApi'

export const SLOTS: readonly Slot[] = ['main', '02', '03', '04', '05', '06', '07', '08', '09']

/** Amazon zooms an image only from this long edge up. */
export const ZOOM_EDGE = 1600

/** Pixii's prices (backend/media/pixii.py LISTING_CREDITS / SCALE_CREDITS_PER_IMAGE). */
export const LISTING_CREDITS = 70
export const SCALE_CREDITS_PER_IMAGE = 10

/** Videos above this are refused by the server (413). */
export const VIDEO_MAX_BYTES = 250 * 1024 * 1024

/** "Main", "Image 2" … "Image 9". */
export function slotLabel(slot: string): string {
  if (slot === 'main') return 'Main'
  const n = Number(slot)
  return Number.isInteger(n) && n >= 2 && n <= 9 ? `Image ${n}` : slot
}

/** Slots in order with `first` (a candidate's suggested slot) leading. */
export function slotsSuggestedFirst(first: Slot | null | undefined): Slot[] {
  return first ? [first, ...SLOTS.filter(s => s !== first)] : [...SLOTS]
}

const ACTIVE: ReadonlySet<string> = new Set(['queued', 'submitting', 'running', 'downloading'])

export function isActiveJob(job: Pick<MediaJob, 'status'>): boolean {
  return ACTIVE.has(job.status)
}

export function hasActiveJobs(jobs: ReadonlyArray<Pick<MediaJob, 'status'>> | null | undefined): boolean {
  return !!jobs && jobs.some(isActiveJob)
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/** One plain line for a job's state. The server sends its own `status_text`
 *  for the engine-specific running lines ("Pixii is making 7 images …"); the
 *  waiting, finished and failed lines are built here so a retry time reads in
 *  the viewer's own clock. */
export function jobStatusText(job: Pick<MediaJob,
  'status' | 'engine' | 'status_text' | 'next_attempt_at' | 'cancel_requested' | 'error_message' | 'candidate_count'
>): string {
  if (job.cancel_requested && isActiveJob(job)) return 'Stopping'
  switch (job.status) {
    case 'queued': {
      const at = parseServerTimestamp(job.next_attempt_at)
      return at ? `Waiting for Pixii, retrying at ${fmtClockTime(at)}` : 'Waiting to start'
    }
    case 'submitting':
      return job.engine === 'pixii' ? 'Sending to Pixii' : 'Starting Codex'
    case 'running':
      return job.status_text || (job.engine === 'pixii'
        ? 'Pixii is making the images'
        : 'Codex is editing the image (2 to 15 minutes)')
    case 'downloading':
      return "Saving Pixii's images"
    case 'done':
      return `Done: ${plural(job.candidate_count ?? 0, 'new image')}`
    case 'cancelled':
      return 'Cancelled'
    case 'timed_out':
      return job.error_message || 'Took too long and was stopped'
    case 'failed':
      return job.error_message || 'Failed'
    default:
      return job.status_text || job.status
  }
}

/** The header's credit line. Off and test mode read as one plain sentence;
 *  otherwise "Pixii credits: 1,230 left · 140 used this week · 3 of 500
 *  requests today". */
export function creditMeterText(status: MediaStatus | null | undefined): string {
  if (!status) return 'Pixii credits: checking…'
  const px = status.pixii
  if (!px.enabled) return 'Pixii is switched off on this server'
  if (!px.configured) return "Pixii isn't set up on this server"
  const requests = `${fmtInt(px.requests_today)} of ${fmtInt(px.daily_limit)} requests today`
  if (px.test_mode) return `Test mode: sample images, no credits used · ${requests}`
  const left = px.credits_remaining == null
    ? "Pixii credits: couldn't check right now"
    : `Pixii credits: ${fmtInt(px.credits_remaining)} left`
  return `${left} · ${fmtInt(px.credits_used_7d)} used this week · ${requests}`
}

/** Can a new job for this engine be queued? `null` when yes, else the reason. */
export function engineOffReason(status: MediaStatus | null | undefined, engine: 'pixii' | 'codex'): string | null {
  const OFF = 'Switched off on this server. Ask an admin to turn on image jobs.'
  if (!status || !status.worker.enabled) return OFF
  if (engine === 'pixii') {
    if (!status.pixii.enabled) return OFF
    if (!status.pixii.configured) return "Pixii isn't set up on this server. Ask an admin to add its key."
    return null
  }
  if (!status.codex.enabled) return OFF
  if (!status.codex.binary_found) return "Codex isn't set up on this server. Ask an admin to install it."
  return null
}

/** Every job engine is off: the tab shows one banner instead of greyed buttons alone. */
export function allEnginesOff(status: MediaStatus | null | undefined): boolean {
  return engineOffReason(status, 'pixii') !== null && engineOffReason(status, 'codex') !== null
}

/** The ticks `candidate` needs for `slot` (the server sends both lists). */
export function requiredTicksFor(candidate: Pick<MediaCandidate, 'required_ticks'>, slot: Slot): RequiredTick[] {
  return (slot === 'main' ? candidate.required_ticks?.main : candidate.required_ticks?.gallery) ?? []
}

/** The automatic checks that count for `slot`: all of them for the main
 *  image, only the size check for a gallery image. */
export function checksForSlot(checks: ReadonlyArray<MediaCheck>, slot: Slot): MediaCheck[] {
  return slot === 'main' ? [...checks] : checks.filter(c => c.id === 'size')
}

/** Mirror of compliance.approval_blockers: a failing automatic check for the
 *  slot, then every required tick that isn't ticked ("Confirm: <label>").
 *  Warnings never block. Empty = the Approve button may be pressed. */
export function clientBlockers(
  candidate: Pick<MediaCandidate, 'checks' | 'required_ticks'>,
  slot: Slot,
  ticks: Readonly<Record<string, boolean>>,
): string[] {
  const out = checksForSlot(candidate.checks ?? [], slot)
    .filter(c => c.status === 'fail')
    .map(c => c.detail || c.label || c.id)
  for (const t of requiredTicksFor(candidate, slot)) {
    if (ticks[t.id] !== true) out.push(`Confirm: ${t.label}`)
  }
  return out
}

/** Short chip text for one automatic check ("1024px (zoom needs 1600)"). */
export function checkChipText(check: MediaCheck, candidate?: Pick<MediaCandidate, 'width' | 'height'>): string {
  switch (check.id) {
    case 'white_background':
      return check.status === 'pass' ? 'White background'
        : check.status === 'warn' ? 'Mostly white background' : "Background isn't white"
    case 'fill': {
      const pct = /(\d+)%/.exec(check.detail ?? '')?.[1]
      return pct ? `Fills ${pct}% of the frame` : 'Product fill'
    }
    case 'square':
      return check.status === 'pass' ? 'Square' : 'Not square'
    case 'size': {
      const edge = candidate ? Math.max(candidate.width ?? 0, candidate.height ?? 0) : 0
      if (check.status === 'fail') return 'Too small for Amazon'
      if (check.status === 'warn') return edge ? `${edge}px (zoom needs ${ZOOM_EDGE})` : `Zoom needs ${ZOOM_EDGE}px`
      return edge ? `${edge}px` : 'Size OK'
    }
    default:
      return check.label
  }
}

/** Insert a shopper keyword, quoted, at `at` (default: the end) with exactly
 *  one space either side and none before punctuation. Returns the new text
 *  and where the caret goes (just after the closing quote). */
export function insertKeyword(text: string, keyword: string, at: number = text.length): { text: string; caret: number } {
  const pos = Math.max(0, Math.min(at, text.length))
  const before = text.slice(0, pos).replace(/\s+$/, '')
  const after = text.slice(pos).replace(/^\s+/, '')
  const quoted = `"${keyword.trim()}"`
  const left = before ? `${before} ${quoted}` : quoted
  const right = !after ? '' : /^[.,;:!?)]/.test(after) ? after : ` ${after}`
  return { text: left + right, caret: left.length }
}

/** The Pending changes fields that go to Amazon through the listing sender:
 *  an Image studio image update (`listing_images`, PR 16) and a Launch tab
 *  listing draft (`listing`, PR 17). */
export const LISTING_FIELDS: ReadonlySet<string> = new Set(['listing_images', 'listing'])

/** A Pending changes row the server cannot send right now: a listing or
 *  listing-image update while listing updates are switched off on the server
 *  (GET /api/launches/status `listing_writes`). Unknown counts as off, so a
 *  failed status read never offers an Apply that the server would refuse. */
export function isHeldTrayRow(edit: Pick<ManualEdit, 'field'>, listingWrites: boolean = false): boolean {
  return LISTING_FIELDS.has(edit.field) && !listingWrites
}

/** Mirror of backend/media/pixii.estimate_credits. Clone slots are counted
 *  once each (the server de-duplicates them the same way). */
export function estimateCredits(kind: JobKind, params: { source_slots?: ReadonlyArray<string> } = {}): number {
  if (kind === 'pixii_listing') return LISTING_CREDITS
  if (kind === 'pixii_clone') return SCALE_CREDITS_PER_IMAGE * Math.max(1, new Set(params.source_slots ?? []).size)
  if (kind === 'pixii_mobile_main') return SCALE_CREDITS_PER_IMAGE
  return 0
}

/** "Pixii set · 7 images · Oct 7, 2:14 PM · by joseph", or "Codex edit of
 *  candidate 12" — the header over one job's candidates. */
export function candidateGroupTitle(
  job: Pick<MediaJob, 'kind' | 'created_at' | 'created_by'> | null,
  candidates: ReadonlyArray<Pick<MediaCandidate, 'origin' | 'parent_candidate_id'>>,
): string {
  const first = candidates[0]
  if (job?.kind === 'codex_edit' || (!job && first?.origin === 'Codex edit')) {
    const parent = candidates.find(c => c.parent_candidate_id != null)?.parent_candidate_id
    return parent != null ? `Codex edit of candidate ${parent}` : 'Codex edit of a current image'
  }
  const parts = [first?.origin ?? 'New images', plural(candidates.length, 'image')]
  const when = parseServerTimestamp(job?.created_at ?? null)
  if (when) parts.push(fmtMonthDayTime(when))
  if (job?.created_by) parts.push(`by ${job.created_by}`)
  return parts.join(' · ')
}

export type CandidateGroup = { jobId: number; job: MediaJob | null; candidates: MediaCandidate[] }

/** Candidates grouped by the job that made them, newest job first; inside a
 *  group, in Pixii's own order. Deleted ones are dropped. */
export function groupCandidates(candidates: ReadonlyArray<MediaCandidate>, jobs: ReadonlyArray<MediaJob>): CandidateGroup[] {
  const byJob = new Map<number, MediaCandidate[]>()
  for (const c of candidates) {
    if (c.status === 'deleted') continue
    const list = byJob.get(c.job_id) ?? []
    list.push(c)
    byJob.set(c.job_id, list)
  }
  const jobById = new Map(jobs.map(j => [j.id, j]))
  return [...byJob.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([jobId, list]) => ({
      jobId,
      job: jobById.get(jobId) ?? null,
      candidates: [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id),
    }))
}

/** "Main, Image 3" for the "Waiting to be sent to Amazon" strip. */
export function slotList(slots: ReadonlyArray<string>): string {
  return slots.map(slotLabel).join(', ')
}

/**
 * Where a current slot's image actually is, as far as Amazon goes.
 *   'amazon'  — read from Amazon, or the mirror's copy of Amazon's image
 *   'queued'  — ClickPay hosts an approved image; its listing update waits in
 *               Pending changes (nothing in this PR sends it)
 *   'clickpay'— ClickPay hosts an approved image and nothing is queued
 *   'empty'   — no image
 * Only 'amazon' may be described as "on Amazon".
 */
export type SlotAmazonState = 'amazon' | 'queued' | 'clickpay' | 'empty'

export function slotAmazonState(
  slot: Pick<LiveSlot, 'url' | 'source' | 'slot'> | null | undefined,
  waiting: ReadonlyArray<string> | ReadonlySet<string> = [],
): SlotAmazonState {
  if (!slot?.url) return 'empty'
  if (slot.source !== 'generated') return 'amazon'
  const has = Array.isArray(waiting) ? waiting.includes(slot.slot) : (waiting as ReadonlySet<string>).has(slot.slot)
  return has ? 'queued' : 'clickpay'
}

/** The tile's visible line for a ClickPay-only image; null when it is on Amazon. */
export function slotAmazonLine(state: SlotAmazonState): string | null {
  if (state === 'clickpay') return 'Not sent to Amazon yet'
  if (state === 'queued') return 'Waiting to be sent to Amazon'
  return null
}

/** The Approve dialog's caption over the current image of a slot. */
export function currentSlotCaption(slot: Pick<LiveSlot, 'url' | 'source' | 'slot'> | null | undefined): string {
  const state = slotAmazonState(slot)
  if (state === 'empty') return 'Now'
  return state === 'amazon' ? 'Now on Amazon' : 'Now on ClickPay (not on Amazon yet)'
}

/** An error from the media API as one sentence plus an optional list (the
 *  422 bodies carry `{message, blockers|errors}`). */
export function studioError(e: unknown): { message: string; items: string[] } {
  const body = (e as { body?: unknown } | null)?.body
  const detail = typeof body === 'object' && body !== null ? (body as { detail?: unknown }).detail : undefined
  if (typeof detail === 'string') return { message: detail, items: [] }
  // FastAPI's own validation errors: a list of {loc, msg} (never shown raw).
  if (Array.isArray(detail)) return { message: 'Check the settings and try again', items: [] }
  if (detail && typeof detail === 'object') {
    const d = detail as { message?: unknown; blockers?: unknown; errors?: unknown }
    const list = Array.isArray(d.blockers) ? d.blockers : Array.isArray(d.errors) ? d.errors : []
    return {
      message: typeof d.message === 'string' ? d.message : 'That didn’t work',
      items: list.filter((x): x is string => typeof x === 'string'),
    }
  }
  const msg = e instanceof Error ? e.message : String(e)
  return { message: msg || 'That didn’t work', items: [] }
}

/** A 10-character ASIN, upper-cased; null when it isn't one. */
export function normalizeAsin(value: string): string | null {
  const v = value.trim().toUpperCase()
  return /^[A-Z0-9]{10}$/.test(v) ? v : null
}
